import { useEffect, useRef, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { toast } from '@/lib/toast';
import { supabase } from '@/integrations/supabase/client';
import { emailSchema } from '@/lib/validationSchemas';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';
import { goldGradientActive } from '@/lib/gradientStyles';
import { GlassModal } from '@/components/ui/glass-modal';

interface ForgotPasswordDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
}

const FIELD_SHELL =
	'rounded-lg bg-white/[0.02] backdrop-blur-xl border border-white/[0.06] hover:border-white/30 focus-within:border-white/40 transition-colors';
const FIELD_INPUT =
	'h-12 bg-transparent border-0 shadow-none focus-visible:ring-0 placeholder:text-white/50 text-base px-4';

export function ForgotPasswordDialog({ open, onOpenChange }: ForgotPasswordDialogProps) {
	const isMobile = useIsMobile();
	const [email, setEmail] = useState('');
	const [loading, setLoading] = useState(false);
	const emailRef = useRef<HTMLInputElement>(null);

	useEffect(() => {
		if (!open) {
			setLoading(false);
			return;
		}
		if (isMobile) return;
		const t = setTimeout(() => emailRef.current?.focus(), 80);
		return () => clearTimeout(t);
	}, [open, isMobile]);

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		const validation = emailSchema.safeParse(email);
		if (!validation.success) {
			toast.error(validation.error.errors[0].message);
			return;
		}

		setLoading(true);
		try {
			const { error } = await supabase.auth.resetPasswordForEmail(
				validation.data.toLowerCase(),
				{ redirectTo: `${window.location.origin}/reset-password` },
			);
			if (error) throw error;

			toast.success('Password reset email sent', {
				description: 'Check your inbox for the reset link.',
			});
			onOpenChange(false);
			setEmail('');
		} catch (error: any) {
			console.error('[ForgotPassword] Error:', error);
			toast.error("Couldn't send the reset email", {
				description: 'Please try again in a moment, or email hello@cardsimp.com if it keeps failing.',
			});
		} finally {
			setLoading(false);
		}
	};

	return (
		<GlassModal
			open={open}
			onOpenChange={onOpenChange}
			title="Reset your password"
			description="Enter your email to receive a password reset link."
		>
			<div className="flex flex-col gap-5">
				<div className="text-center space-y-1.5">
					<h2 className="font-sans text-2xl font-bold tracking-tight leading-none [text-shadow:0_1px_2px_rgba(0,0,0,0.55),0_2px_10px_rgba(0,0,0,0.35)]">
						Reset your password
					</h2>
					<p className="text-xs text-muted-foreground">
						We'll email you a link to set a new one.
					</p>
				</div>

				<form onSubmit={handleSubmit} className="space-y-3">
					<div className={FIELD_SHELL}>
						<Input
							ref={emailRef}
							type="email"
							name="email"
							autoComplete="email"
							inputMode="email"
							enterKeyHint="go"
							placeholder="your@email.com"
							value={email}
							onChange={(e) => setEmail(e.target.value)}
							className={FIELD_INPUT}
							disabled={loading}
							required
							aria-label="Email address for password reset"
						/>
					</div>

					<Button
						type="submit"
						className={cn('w-full h-12 text-base', goldGradientActive)}
						disabled={loading}
					>
						{loading ? 'Sending…' : 'Send reset link'}
					</Button>
				</form>
			</div>
		</GlassModal>
	);
}
